import {useState} from 'react';
import {clsx} from 'clsx';
import {useForm} from '@tanstack/react-form';
import {ArticleMultilayerPlan} from '../../envisia/article/main/multilayer-plan/article-multilayer-plan.models';
import {EvButton} from '../form/EvButton';
import {EvInputField} from '../form/EvInputField';
import {useMultilayerPlanUpdateNameMutation} from './use-multilayer-plan-update-name-mutation';
import {useMultilayerPlanDeleteMutation} from './use-multilayer-plan-delete-mutation';
import {useMultilayerPlanPdfMutation} from './use-multilayer-plan-pdf-mutation';

interface Props {
  plan: ArticleMultilayerPlan;
  selected: boolean;
  select: (sharedId: string | null) => void;
}

export function MultilayerPlanListRow(props: Props) {
  const [editingName, setEditingName] = useState<boolean>(false);
  const {openPdf} = useMultilayerPlanPdfMutation({id: props.plan.id});
  const {updateNameMutate, updateNameIsPending} = useMultilayerPlanUpdateNameMutation({
    onSuccess: () => {
      setEditingName(false);
    },
  });

  const {deleteMutation, deleteIsPending} = useMultilayerPlanDeleteMutation({
    sharedId: props.plan.shared_id,
    onSuccess: () => {
      if (props.selected) {
        props.select(null);
      }
    },
  });

  const remove = () => {
    if (!confirm(`Soll der Aufbauplan "${props.plan.name}" wirklich dauerhaft gelöscht werden?`)) {
      return;
    }

    deleteMutation();
  };

  const form = useForm({
    defaultValues: {name: props.plan.name},
    onSubmit: ({value}) => {
      if (!value.name) {
        return;
      }

      updateNameMutate({shared_id: props.plan.shared_id, name: value.name});
    },
  });

  return <div className={clsx(
    {'font-bold': props.selected},
    'grid',
    'col-span-5',
    'grid-cols-subgrid',
    'bg-storz-whitetable-background',
    'border',
    'border-solid',
    'border-storz-whitetable-border',
    'border-b-0',
    'last:border-b',
  )}>
    <div className="p-2 text-right">{props.plan.id}</div>
    <div className="p-2">
      {!editingName ?
        <>
          {props.plan.name}&nbsp;
          <EvButton variant="link"
                    size="no-padding"
                    title="Bezeichnung bearbeiten"
                    disabled={deleteIsPending}
                    onClick={() => setEditingName(true)}>
            <i className="fa fa-pencil"/>
          </EvButton>
        </> :
        <form className="flex items-center gap-1"
              onSubmit={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                await form.handleSubmit();
              }}>
          <form.Field name="name">
            {(field) => <EvInputField type="text" formStyle="sm" field={field} autoFocus/>}
          </form.Field>
          <EvButton type="submit"
                    variant="link"
                    size="no-padding"
                    title="Bezeichnung speichern"
                    disabled={updateNameIsPending}>
            <i className="fa fa-save"/>
          </EvButton>
          <EvButton variant="link"
                    size="no-padding"
                    title="Abbrechen"
                    disabled={updateNameIsPending}
                    onClick={() => setEditingName(false)}>
            <i className="fa fa-times"/>
          </EvButton>
        </form>
      }
    </div>

    <div className="p-2">
      <EvButton variant="link"
                size="no-padding"
                title="Aufbauplan öffnen"
                onClick={() => props.select(props.plan.shared_id)}>
        <i className="fa fa-file-o"/>
      </EvButton>
    </div>

    <div className="p-2">
      <EvButton variant="link"
                size="no-padding"
                title="PDF öffnen"
                onClick={() => openPdf()}>
        <i className="fa fa-file-pdf-o"/>
      </EvButton>
    </div>

    <div className="p-2">
      <EvButton variant="link"
                size="no-padding"
                title="Aufbauplan löschen"
                disabled={deleteIsPending}
                onClick={() => remove()}>
        <i className="fa fa-trash-o text-storz-red"/>
      </EvButton>
    </div>
  </div>;
}
